/**
 * Migration engine for tests
 *
 * This module provides utilities to run migrations from entity definitions,
 * primarily used in integration tests to set up the database schema.
 *
 * For production, use the file-based migration system (migration-runner.ts).
 */
import type { Knex } from 'knex'
import type { ModuleManifest, ModuleContext, EntityDefinition, FieldDefinition, FieldDbConfig, EntityIndex } from '@gzl10/nexus-sdk'

/**
 * Entity types that have their own database table.
 */
const PERSISTENT_TYPES = new Set([
  'collection',
  'tree',
  'dag',
  'event',
  'reference',
  'config',
  'temp',
])

/**
 * Create all tables from all modules in FK-dependency order.
 * Collects all entity definitions, sorts by FK references, and creates inline.
 * This ensures cross-module FKs work correctly (including SQLite).
 */
export async function runAllGeneratedMigrations(
  ctx: ModuleContext,
  modules: ModuleManifest[]
): Promise<void> {
  if (process.env['NODE_ENV'] !== 'test') {
    throw new Error('runAllGeneratedMigrations() is only allowed in test mode.')
  }

  const db = ctx.db?.knex ?? (ctx as unknown as { db: Knex }).db

  // Collect all persistent entities with table names
  const entities: Array<{ tableName: string; entityDef: EntityDefinition & { table?: string } }> = []

  for (const mod of modules) {
    if (!mod.definitions) continue
    for (const def of mod.definitions) {
      const entityType = def.type ?? 'collection'
      if (!PERSISTENT_TYPES.has(entityType)) continue
      const entityDef = def as EntityDefinition & { table?: string }
      if (!entityDef.table) continue
      entities.push({ tableName: entityDef.table, entityDef })
    }
  }

  // Sort entities: tables without FK deps first, then tables that reference already-created tables
  const sorted = topologicalSortEntities(entities)

  for (const { tableName, entityDef } of sorted) {
    // Skip if table already exists
    const exists = await db.schema.hasTable(tableName)
    if (exists) continue

    // Create table with FKs inline
    await db.schema.createTable(tableName, (table) => {
      const fieldNames = new Set(Object.keys(entityDef.fields ?? {}))
      if (entityDef.fields) {
        for (const [fieldName, field] of Object.entries(entityDef.fields)) {
          if (!field.db || field.db.virtual) continue
          addColumn(table, fieldName, field, db)
        }
      }

      // Auto-fields: skip if already declared in fields
      const hasTimestamps = (entityDef as EntityDefinition & { timestamps?: boolean }).timestamps
      if (hasTimestamps) {
        if (!fieldNames.has('created_at')) table.timestamp('created_at').nullable().defaultTo(db.fn.now())
        if (!fieldNames.has('updated_at')) table.timestamp('updated_at').nullable().defaultTo(db.fn.now())
      }

      const hasAudit = (entityDef as EntityDefinition & { audit?: boolean }).audit
      if (hasAudit) {
        if (!fieldNames.has('created_by')) table.string('created_by', 26).nullable()
        if (!fieldNames.has('updated_by')) table.string('updated_by', 26).nullable()
      }

      // Auto-field: soft delete
      const hasSoftDelete = (entityDef as EntityDefinition & { softDelete?: boolean }).softDelete
      if (hasSoftDelete) {
        if (!fieldNames.has('deleted_at')) table.timestamp('deleted_at').nullable()
      }

      // Auto-field: tree parent reference
      if (entityDef.type === 'tree') {
        table.string('parent_id', 26).nullable().references(`${tableName}.id`).onDelete('SET NULL')
      }
    })

    // Create entity-level indexes
    const entityIndexes = (entityDef as EntityDefinition & { indexes?: EntityIndex[] }).indexes
    if (entityIndexes?.length) {
      await db.schema.alterTable(tableName, (table) => {
        for (const idx of entityIndexes) {
          if (idx.unique) {
            table.unique(idx.columns)
          } else {
            table.index(idx.columns)
          }
        }
      })
    }
  }
}

/**
 * Topological sort of entities by FK references.
 * Entities that reference other tables come after the referenced tables.
 */
function topologicalSortEntities(
  entities: Array<{ tableName: string; entityDef: EntityDefinition & { table?: string } }>
): Array<{ tableName: string; entityDef: EntityDefinition & { table?: string } }> {
  const tableSet = new Set(entities.map(e => e.tableName))

  // Build dependency graph: tableName → set of tables it references
  const deps = new Map<string, Set<string>>()
  for (const { tableName, entityDef } of entities) {
    const refs = new Set<string>()
    if (entityDef.fields) {
      for (const field of Object.values(entityDef.fields)) {
        const relation = (field as FieldDefinition & { relation?: { table: string } }).relation
        if (relation && tableSet.has(relation.table) && relation.table !== tableName) {
          refs.add(relation.table)
        }
      }
    }
    deps.set(tableName, refs)
  }

  // Kahn's algorithm
  const sorted: Array<{ tableName: string; entityDef: EntityDefinition & { table?: string } }> = []
  const entityMap = new Map(entities.map(e => [e.tableName, e]))
  const inDegree = new Map<string, number>()

  for (const [table, tableDeps] of deps) {
    inDegree.set(table, tableDeps.size)
  }

  const queue: string[] = []
  for (const [table, degree] of inDegree) {
    if (degree === 0) queue.push(table)
  }

  while (queue.length > 0) {
    const table = queue.shift()!
    const entity = entityMap.get(table)
    if (entity) sorted.push(entity)

    // Reduce in-degree for dependents
    for (const [other, otherDeps] of deps) {
      if (otherDeps.has(table)) {
        otherDeps.delete(table)
        const newDegree = (inDegree.get(other) ?? 1) - 1
        inDegree.set(other, newDegree)
        if (newDegree === 0) queue.push(other)
      }
    }
  }

  // Add any remaining (circular deps) at the end
  for (const entity of entities) {
    if (!sorted.some(s => s.tableName === entity.tableName)) {
      sorted.push(entity)
    }
  }

  return sorted
}

/**
 * @deprecated Use runAllGeneratedMigrations instead.
 * Kept for backward compatibility with tests that use it directly.
 */
export async function runGeneratedMigration(
  ctx: ModuleContext,
  module: ModuleManifest
): Promise<void> {
  await runAllGeneratedMigrations(ctx, [module])
}

/**
 * Add a column to a table based on field definition.
 * FKs are applied inline (works for all DBs including SQLite).
 */
function addColumn(
  table: Knex.CreateTableBuilder,
  fieldName: string,
  field: FieldDefinition,
  db: Knex
): void {
  const columnName = fieldName
  const dbConfig = field.db as FieldDbConfig

  let column: Knex.ColumnBuilder

  switch (dbConfig.type) {
    case 'string':
      column = table.string(columnName, dbConfig.size || 255)
      break
    case 'text':
      column = table.text(columnName)
      break
    case 'integer':
      column = table.integer(columnName)
      break
    case 'decimal': {
      const precision = dbConfig.precision
      if (Array.isArray(precision)) {
        column = table.decimal(columnName, precision[0], precision[1])
      } else {
        column = table.decimal(columnName, precision || 10, 2)
      }
      break
    }
    case 'boolean':
      column = table.boolean(columnName)
      break
    case 'date':
      column = table.date(columnName)
      break
    case 'datetime':
      column = table.timestamp(columnName)
      break
    case 'json':
      column = table.json(columnName)
      break
    case 'uuid':
      column = table.uuid(columnName)
      break
    case 'array':
      column = table.json(columnName)
      break
    default:
      column = table.string(columnName, 255)
  }

  // Handle nullable
  if (dbConfig.nullable === false) {
    column.notNullable()
  } else {
    column.nullable()
  }

  // Handle primary key
  if (dbConfig.primary || dbConfig.idType || columnName === 'id') {
    column.primary()
  }

  // Handle unique
  if (dbConfig.unique) {
    column.unique()
  }

  // Handle default value
  if (dbConfig.default !== undefined) {
    // For JSON/array columns, Knex MySQL needs an object/array (not a string)
    // to generate the correct DEFAULT ('...') expression
    const isJsonColumn = dbConfig.type === 'json' || dbConfig.type === 'array'
    if (isJsonColumn && typeof dbConfig.default === 'string') {
      try {
        column.defaultTo(JSON.parse(dbConfig.default))
      } catch {
        column.defaultTo(dbConfig.default)
      }
    } else {
      column.defaultTo(dbConfig.default)
    }
  } else if (dbConfig.defaultFn === 'now') {
    column.defaultTo(db.fn.now())
  }

  // Handle foreign key via relation config (inline)
  const relation = (field as FieldDefinition & { relation?: { table: string; column?: string; onDelete?: string; onUpdate?: string } }).relation
  if (relation) {
    const fkRef = column.references(relation.column || 'id').inTable(relation.table)
    if (relation.onDelete) {
      fkRef.onDelete(relation.onDelete)
    }
    if (relation.onUpdate) {
      fkRef.onUpdate(relation.onUpdate)
    }
  }
}
